import { useNavigate, useParams } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import { IPageProps, IParamsId, IRootState, IDealerChangedEvent } from 'types'
import { FormDropDownList, FormInput, PageHeader, ReturnButton, SaveButton, Spinner } from 'components'
import DealerSelect from 'components/Dealers/DealerSelect'
import { useCallback, useEffect, useState } from 'react'
import { Form, FormElement, Field, FieldWrapper } from '@progress/kendo-react-form'
import { ColorPicker, ColorPickerChangeEvent } from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'state/store'
import { V1ApiIntegrationsClient } from 'core/http-common'
import { AudioEyeSiteDto, UpdateAudioEyeSiteDto } from 'generated/v1/v1IntegrationsApi'
import toast from 'core/toast'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { RequiredValidator } from 'core/Validators'
import { toHexColor } from 'core/helpers'

interface IStringDropDownValue {
    id: string
    text: string
}

const locationOptions: IStringDropDownValue[] = [
    { id: 'right-lower', text: 'Lower Right' },
    { id: 'right-mid-lower', text: 'Lower Mid Right' },
    { id: 'right-middle', text: 'Middle Right' },
    { id: 'right-mid-upper', text: 'Upper Mid Right' },
    { id: 'right-upper', text: 'Upper Right' },
    { id: 'left-lower', text: 'Lower Left' },
]

const toolbarThemeOptions: IStringDropDownValue[] = [
    { id: 'light', text: 'Light' },
    { id: 'dark', text: 'Dark' },
]

const AudioEyeEditPage = ({ title }: IPageProps) => {

    const { id } = useParams<IParamsId>()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const redirectUrl = '/integrations/audioeye'
    const loading = useSelector((state: IRootState) => state.app.loading)

    const [site, setSite] = useState<AudioEyeSiteDto | undefined>(undefined)
    const [loaded, setLoaded] = useState(false)
    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(undefined)
    const [hexColor, setHexColor] = useState<string>('#000000')

    const loadSite = useCallback(async () => {
        if (!id) return

        try {
            const result = await V1ApiIntegrationsClient.getSite(id)
            setSite(result)
            setSelectedDealerId(result.dealerId)
            setHexColor(toHexColor(result.color))
            setLoaded(true)
        } catch (e) {
            toast.responseErrors(e)
            navigate(redirectUrl)
        }
    }, [id, navigate])

    useEffect(() => {
        loadSite()
    }, [loadSite])

    const handleDealerChange = (event: IDealerChangedEvent) => {
        setSelectedDealerId(event.dealerId)
    }

    const handleDealerClear = () => {
        setSelectedDealerId(undefined)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSubmit = async (values: any) => {
        if (!id || !selectedDealerId) return

        try {
            await dispatch(startLoadingAction())

            const dto = new UpdateAudioEyeSiteDto({
                dealerId: selectedDealerId,
                websiteUrl: values.websiteUrl,
                location: values.location?.id ?? values.location,
                toolbarTheme: values.toolbarTheme?.id ?? values.toolbarTheme,
                color: hexColor
            })

            await V1ApiIntegrationsClient.updateSite(id, dto)
            toast.success('AudioEye site updated successfully')
            navigate(redirectUrl)
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    if (!loaded) {
        return (
            <div id='AudioEyeEditPage'>
                <PageHeader title={title}>
                    <ReturnButton redirectUrl={redirectUrl} />
                </PageHeader>
                <Spinner center />
            </div>
        )
    }

    return (
        <div id='AudioEyeEditPage'>
            <PageHeader title={title}>
                <ReturnButton redirectUrl={redirectUrl} />
            </PageHeader>

            <Form
                onSubmit={handleSubmit}
                ignoreModified={true}
                initialValues={{
                    websiteUrl: site?.websiteUrl,
                    location: locationOptions.find(o => o.id === site?.location) ?? locationOptions[0],
                    toolbarTheme: toolbarThemeOptions.find(o => o.id === site?.toolbarTheme) ?? toolbarThemeOptions[0],
                }}
                render={(formRenderProps) => (
                    <FormElement>
                        <div className='bg-light border p-3 mb-3'>
                            <div className='bg-white border p-3'>
                                <Row>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='dealer'
                                            label='Dealer:'
                                            component={DealerSelect}
                                            dealerId={selectedDealerId ?? 0}
                                            onDealerChanged={handleDealerChange}
                                            onDealerCleared={handleDealerClear}
                                            required={true}
                                            disabled={true}
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='websiteUrl'
                                            label='Website Url:'
                                            component={FormInput}
                                            className='form-control'
                                            required
                                            validator={RequiredValidator}
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='location'
                                            label='Location on Website:'
                                            component={FormDropDownList}
                                            data={locationOptions}
                                            textField='text'
                                            dataItemKey='id'
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <Field
                                            name='toolbarTheme'
                                            label='Toolbar Theme:'
                                            component={FormDropDownList}
                                            data={toolbarThemeOptions}
                                            textField='text'
                                            dataItemKey='id'
                                        />
                                    </Col>
                                    <Col lg={6} md={6}>
                                        <FormLabelValue label='Status:' value={site?.status === 'active' ? 'Active' : 'Cancelled'} />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={6} md={6}>
                                        <FieldWrapper>
                                            <Label>Hex Color for Icon:</Label>
                                            <ColorPicker
                                                value={hexColor}
                                                onChange={(e: ColorPickerChangeEvent) => setHexColor(toHexColor(e.value as string))}
                                                view='palette'
                                            />
                                        </FieldWrapper>
                                    </Col>
                                </Row>
                                <Row className='mt-3'>
                                    <Col>
                                        <SaveButton
                                            type='submit'
                                            className='btn-lg btn-primary btnWidth'
                                            disabled={!formRenderProps.allowSubmit || loading}>
                                            Save
                                        </SaveButton>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </FormElement>
                )}
            />
        </div>
    )
}

const FormLabelValue = ({ label, value }: { label: string, value?: string }) => (
    <FieldWrapper>
        <Label>{label}</Label>
        <div className='form-control-plaintext'>{value}</div>
    </FieldWrapper>
)

export default AudioEyeEditPage
